import { useState, useEffect, useCallback } from "react";
import { api, getToken, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Send, Paperclip, Mail, ShieldCheck, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SendEmailDialog({ open, onOpenChange, nda, msStatus, onConnect, onSent }) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");

  const load = useCallback(async () => {
    if (!nda) return;
    setLoading(true);
    try {
      const r = await api.get(`/ndas/${nda.id}/teaser-preview`);
      setTo(r.data.to || nda.email || "");
      setSubject(r.data.subject || "");
      setHtml(r.data.html || "");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [nda]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const connected = msStatus?.connected;
  const configured = msStatus?.configured;

  const send = async () => {
    if (!to.trim()) return toast.error("Renseignez une adresse email destinataire");
    if (!connected) return toast.error("Connectez d'abord votre compte Microsoft");
    setSending(true);
    try {
      const r = await api.post(`/ndas/${nda.id}/send-email`, { to: to.trim(), subject: subject.trim() });
      toast.success(`Email envoyé à ${r.data.sent_to} via Outlook`);
      onOpenChange(false);
      onSent && onSent(nda.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
          onClick={() => onOpenChange(false)}
          data-testid="send-email-backdrop"
        />
      )}
      <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
        <DialogContent
          className="max-h-[92vh] max-w-3xl overflow-y-auto"
          data-testid="send-email-dialog"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#0F172A]">Envoyer le teaser + NDA</DialogTitle>
          <DialogDescription className="text-slate-500">
            Aperçu de l'email envoyé depuis votre compte Microsoft Outlook, avec le NDA en pièce jointe.
          </DialogDescription>
        </DialogHeader>

        {/* Microsoft connection status */}
        {!configured ? (
          <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="ms-not-configured">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            <div className="text-sm text-amber-800">
              <p className="font-semibold">Microsoft non configuré</p>
              <p>Les identifiants Azure (Client ID / Secret) doivent être ajoutés pour activer l'envoi.</p>
            </div>
          </div>
        ) : connected ? (
          <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800" data-testid="ms-connected-banner">
            <ShieldCheck className="h-4 w-4" /> Connecté en tant que <span className="font-semibold">{msStatus.email || "compte Microsoft"}</span>
          </div>
        ) : (
          <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="ms-connect-banner">
            <div className="flex items-start gap-3 text-sm text-slate-600">
              <Mail className="mt-0.5 h-5 w-5 shrink-0 text-[#0F172A]" />
              <span>Connectez votre compte Microsoft Outlook pour envoyer cet email depuis votre adresse.</span>
            </div>
            <Button onClick={onConnect} data-testid="ms-connect-btn" className="rounded-full bg-[#0F172A] text-white hover:bg-[#1e293b]">
              Connecter mon compte Microsoft
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label className="text-slate-700">Destinataire</Label>
            <Input data-testid="email-to" value={to} onChange={(e) => setTo(e.target.value)} disabled={loading} className="mt-1.5 h-10" placeholder="email@exemple.com" />
          </div>
          <div>
            <Label className="text-slate-700">Objet</Label>
            <Input data-testid="email-subject" value={subject} onChange={(e) => setSubject(e.target.value)} disabled={loading} className="mt-1.5 h-10" />
          </div>
        </div>

        <div className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600" data-testid="email-attachment">
          <Paperclip className="h-4 w-4 text-[#C6A87C]" />
          Pièce jointe : <span className="font-medium text-[#0F172A]">NDA-{nda?.first_name}-{nda?.last_name}.pdf</span>
        </div>

        <div>
          <Label className="text-slate-700">Aperçu de l'email</Label>
          <div className="mt-1.5 overflow-hidden rounded-xl border border-slate-200 bg-white">
            {loading ? (
              <div className="flex h-64 items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
            ) : (
              <iframe title="Aperçu email" srcDoc={html} data-testid="email-preview-frame" className="h-[420px] w-full" />
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full" data-testid="email-cancel">Annuler</Button>
          <Button onClick={send} disabled={sending || !connected || loading} className="rounded-full bg-[#C6A87C] text-[#0F172A] hover:bg-[#b8986a]" data-testid="email-send">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-2 h-4 w-4" /> Envoyer maintenant</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
